import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Button, Card, CardBody, CardFooter, Container, FormGroup, Input, Label, Progress, Row } from "reactstrap";
import Header from "../../components/Header";
import LoadedContent from "../../components/LoadedContent";
import { useService } from "../../context/ServiceContext";

const COEFFICIENT_OPTIONS = Array.from({ length: 21 }, (_, i) => 85 + i); // 85, 86, ..., 105

function PitchAngleGenerator(props) {
  const { pitchAngleGenerationService } = useService();
  const [loaded, setLoaded] = useState(false);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [finished, setFinished] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [completedCount, setCompletedCount] = useState(0);
  const [count, setCount] = useState(0);
  const [percentageCompleted, setPercentageCompleted] = useState(0);
  const [messages, setMessages] = useState([]);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [coefficientPercent, setCoefficientPercent] = useState(100);

  useEffect(() => {
    (async () => {
      await pitchAngleGenerationService.connect(handleProgressUpdate);
      let progress = await pitchAngleGenerationService.getProgress();
      handleProgressUpdate(progress);

      setLoaded(true);
    })();
  }, []);

  // Fallback polling: SignalR is the primary progress channel, but a run can end without the terminal
  // "PitchAngleFinished" push reaching the client (e.g. the connection drops, or the server aborts a run
  // when the file logger is disposed mid-run). Without a fallback the UI would stay on "Processing" forever.
  // While a run appears active, poll /status every few seconds and reconcile — the server's Progress always
  // reflects the true terminal state (isFinished=true) once the run's finally block runs.
  useEffect(() => {
    if (!started || finished || cancelled) return;

    const intervalId = setInterval(async () => {
      try {
        const progress = await pitchAngleGenerationService.getProgress();
        handleProgressUpdate(progress);
      } catch {
        // transient errors are fine — the next tick retries
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [started, finished, cancelled]);

  async function startProcess() {
    await pitchAngleGenerationService.start(coefficientPercent / 100);
    setStarted(true);
    setCancelled(false);
  }

  async function cancelProcess() {
    await pitchAngleGenerationService.cancel();
    setCancelled(true);
  }

  function handleProgressUpdate(progress) {
    if (!progress) return;

    setFinished(progress.isFinished);
    setFailed(progress.isFailed);
    setCancelled(progress.isCancelled);
    setCount(progress.count || 0);
    setCompletedCount(progress.completedCount || 0);
    setPercentageCompleted(progress.percentageCompleted || 0);
    setMessages(progress.messages || []);
    setErrors(progress.errorMessages || []);
    setWarnings(progress.warningMessages || []);
    if (!started) setStarted(true);
  }

  function renderProgress() {
    const renderIcon = () => {
      // if (failed || errors.length > 0) return <i className="fas fa-times-circle text-danger"></i>;
      if (cancelled) return <i className="fas fa-ban text-warning"></i>;
      if (finished && errors.length == 0) return <i className="fas fa-check-circle text-success"></i>;
      return <i className="fas fa-spinner fa-pulse"></i>;
    };
      const renderMessage = () => {
      if (false && (failed || errors.length > 0)) {
        return <FormattedMessage id="pitch_angle.failed_x_of_y" defaultMessage="Failed processing" values={[completedCount, count]} />;
      } else if (cancelled) {
        return <FormattedMessage id="pitch_angle.cancelled_x_of_y" defaultMessage="Cancelled" values={[completedCount, count]} />;
      } else if (finished) {
        return <FormattedMessage id="pitch_angle.finished_x_of_y" defaultMessage="Finished processing" values={[completedCount, count]} />;
      } else {
        return <FormattedMessage id="pitch_angle.processing_x_of_y" defaultMessage="Processing" values={[completedCount, count]} />;
      }
    };


    return (
      <>
        <h2 className="text-center">
          {finished ? (
            <FormattedMessage id="pitch_angle.finished_generating" defaultMessage="Finished generating" />
          ) : cancelled ? (
            <FormattedMessage id="pitch_angle.cancelled_generating" defaultMessage="Generation cancelled" />
          ) : (
            <FormattedMessage id="pitch_angle.generating_pitch_angle_tests" defaultMessage="Generating pitch angle idEnsayos..." />
          )}
        </h2>

        {renderIcon()}
        <span className="ml-1">{renderMessage()}</span>
        <Progress value={percentageCompleted} className="mt-1" color={finished && errors.length == 0 ? "success" : cancelled ? "warning" : "primary"} />

        {messages &&
          messages.map((m, idx) => (
            <div key={idx}>
              <FormattedMessage id={m.id} defaultMessage={m.defaultMessage} />
            </div>
          ))}
      </>
    );
  }

  function renderStart() {
    return (
      <>
        <h2 className="text-center">
          <FormattedMessage id="pitch_angle.generate_pitch_angle" defaultMessage="Generate pitch angle idEnsayos" />
        </h2>
        <p>
          <FormattedMessage id="pitch_angle.click_run_to_start" defaultMessage="Click 'Run' to generate new pitch angle curves." />
        </p>
        <FormGroup style={{ maxWidth: 280 }}>
          <Label for="bulkInterpolationCoefficient">
            <FormattedMessage id="pitch_angle.interpolation_coefficient" defaultMessage="Interpolation coefficient" />
          </Label>
          <Input
            id="bulkInterpolationCoefficient"
            type="select"
            value={coefficientPercent}
            onChange={(e) => setCoefficientPercent(Number(e.target.value))}
          >
            {COEFFICIENT_OPTIONS.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </Input>
        </FormGroup>
      </>
    );
  }

  function renderRunButtons() {
    const renderIcon = () => {
      if (finished) return <i className="fas fa-redo-alt"></i>;
      if (started && !finished && !cancelled) return <i className="fas fa-spinner fa-pulse"></i>;
      return <i className="far fa-play-circle"></i>;
    };

    return (
      <>
        <Button className="btn-outline-primary mr-2" onClick={startProcess} disabled={started && !finished && !cancelled}>
          {renderIcon()} <FormattedMessage id="pitch_angle.run" defaultMessage="Run" />
        </Button>
        {started && !finished && !cancelled && (
          <Button className="btn-outline-warning" onClick={cancelProcess}>
            <i className="fas fa-ban"></i> <FormattedMessage id="pitch_angle.cancel" defaultMessage="Cancel" />
          </Button>
        )}
      </>
    );
  }

  function renderErrorMessages() {
    if (errors.length === 0) return null;
    return (
      <div className="mt-2">
        <h5>
          <FormattedMessage id="pitch_angle.errors_occurred" defaultMessage="The following errors occurred:" />
        </h5>
        {errors.map((err, idx) => (
          <div key={idx} className="text-danger">
            {err}
          </div>
        ))}
      </div>
    );
  }

  function renderWarningMessages() {
    if (warnings.length === 0) return null;
    return (
      <div className="mt-3">
        <h5 className="text-warning">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          <FormattedMessage
            id="pitch_angle.warnings_occurred"
            defaultMessage="The following warnings need review (idEnsayos were still created):"
          />
        </h5>
        {warnings.map((w, idx) => (
          <div
            key={idx}
            className="text-warning"
            style={{ whiteSpace: "pre-wrap", borderLeft: "3px solid #f0ad4e", paddingLeft: "10px", marginBottom: "6px" }}
          >
            {w}
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <Header>
        <h1>
          <FormattedMessage id="pitch_angle.pitch_angle_curve_generator" defaultMessage="Pitch Angle Curve Generator" />
        </h1>
      </Header>

      <Container fluid>
        <LoadedContent loading={!loaded}>
          <Card>
            <CardBody className="px-4">
              <div className="px-4">{started ? renderProgress() : renderStart()}</div>
              <div className="px-4">{renderErrorMessages()}</div>
              <div className="px-4">{renderWarningMessages()}</div>              
            </CardBody>
            <CardFooter>
              <div className="px-4">{renderRunButtons()}</div>
            </CardFooter>
          </Card>
        </LoadedContent>
      </Container>
    </>
  );
}

export default PitchAngleGenerator;
